import { Seo } from "@/components/common/Seo";
import { PageHero } from "@/components/common/Kinetic";
import { Reveal, SectionHead } from "@/components/common/Reveal";
import { LogoFull } from "@/components/layout/Logo";
import { WhyQuantivo } from "@/components/home/WhyQuantivo";
import { FinalCta } from "@/components/home/FinalCta";

const PRINCIPLES = [
  { t: "Understand before building", d: "We ask how work actually happens today, who uses the system and what success looks like before writing a line of code." },
  { t: "Simple, maintainable code", d: "Readable structure, sensible naming and documentation, so the software stays easy to change long after launch." },
  { t: "Architecture that can grow", d: "We plan for more users, more data and new integrations without over-engineering the first release." },
  { t: "Reliability by default", d: "Validation, error handling, logging and testing are part of the build, not an optional extra." },
];

const APPROACH = [
  { t: "Written scope", d: "Every engagement starts with a written understanding of goals, features and priorities that both sides agree on." },
  { t: "Visible progress", d: "Work is broken into milestones you can review, so feedback happens early rather than at the end." },
  { t: "Plain-language communication", d: "Technical decisions are explained in business terms, including trade-offs and alternatives." },
  { t: "Support after launch", d: "We stay available for maintenance, improvements and new requirements as your business evolves." },
];

const Mission = () => (
  <section className="bg-white py-24 lg:py-32" data-testid="about-mission">
    <div className="container-q grid gap-14 lg:grid-cols-12">
      <div className="lg:col-span-5"><SectionHead index="01" label="Mission" title="Useful software, built to last." /></div>
      <Reveal delay={0.1} className="space-y-6 text-base leading-relaxed text-slate-600 md:text-lg lg:col-span-7">
        <p className="text-xl font-semibold leading-relaxed text-slate-950 md:text-2xl">Quantivo exists to help businesses turn ideas and operational challenges into reliable software that people actually use.</p>
        <p>We are a software development and technology solutions company based in Kolkata, India, working with startups, SMEs and growing businesses in India and internationally. Our work spans SaaS products, custom business applications, web and mobile apps, automation systems and payment-driven platforms.</p>
        <p>We measure our work by whether it solves the problem it was built for: fewer manual steps, clearer information, smoother customer experiences and systems that can keep up as the business grows.</p>
      </Reveal>
    </div>
  </section>
);

const Grid = ({ id, index, label, title, items, dark }) => (
  <section className={`py-24 lg:py-32 ${dark ? "bg-[#050b14] text-white" : "bg-[#f8fafc]"}`} data-testid={id}>
    <div className="container-q">
      <SectionHead dark={dark} index={index} label={label} title={title} />
      <div className={`mt-14 grid gap-px overflow-hidden rounded-lg border sm:grid-cols-2 ${dark ? "border-white/10 bg-white/10" : "border-slate-200 bg-slate-200"}`}>
        {items.map((p, i) => (
          <Reveal key={p.t} delay={i * 0.05} className={`p-8 sm:p-10 ${dark ? "bg-[#050b14]" : "bg-white"}`}>
            <span className={`font-mono text-[12px] ${dark ? "text-cyan-300" : "text-blue-700"}`}>0{i + 1}</span>
            <h3 className={`mt-4 text-xl font-bold tracking-tight ${dark ? "text-white" : "text-slate-950"}`}>{p.t}</h3>
            <p className={`mt-3 text-[15px] leading-relaxed ${dark ? "text-slate-400" : "text-slate-600"}`}>{p.d}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Identity = () => (
  <section className="bg-white py-24 lg:py-32" data-testid="about-identity">
    <div className="container-q grid items-center gap-14 lg:grid-cols-12">
      <Reveal className="lg:col-span-6">
        <div className="grid-light flex items-center justify-center rounded-xl border border-slate-200 bg-white p-10 sm:p-16">
          <LogoFull variant="light" className="h-auto w-full max-w-[380px]" />
        </div>
      </Reveal>
      <div className="lg:col-span-6">
        <SectionHead index="04" label="Commitment" title="Innovate. Automate. Scale." />
        <Reveal delay={0.15} className="mt-6 space-y-5 text-[15px] leading-relaxed text-slate-600 md:text-base">
          <p><strong className="text-slate-950">Innovate</strong> means finding the practical idea that makes a product genuinely better for its users.</p>
          <p><strong className="text-slate-950">Automate</strong> means removing the repetitive, error-prone work that holds teams back.</p>
          <p><strong className="text-slate-950">Scale</strong> means building foundations that support more customers, data and features without starting over.</p>
        </Reveal>
      </div>
    </div>
  </section>
);

export default function About() {
  return (
    <>
      <Seo path="/about" title="About Quantivo — Software Engineering Partner in Kolkata"
        description="Quantivo is a Kolkata-based software company building SaaS, custom software, automation and API solutions for startups and businesses in India and abroad." />
      <PageHero testId="about-hero" eyebrow="About Quantivo" lines={["We build software", "businesses can rely on."]}
        text="An engineering-led team focused on clear scope, maintainable architecture and long-term partnerships." />
      <Mission />
      <Grid id="about-philosophy" index="02" label="Engineering philosophy" title="How we think about code." items={PRINCIPLES} dark />
      <Grid id="about-approach" index="03" label="Service approach" title="How we work with clients." items={APPROACH} />
      <Identity />
      <WhyQuantivo index="05" />
      <FinalCta />
    </>
  );
}
